import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { MapPin, Phone, Mail, Clock, ArrowRight, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Reveal, SectionLabel, MaskedLine } from "../components/Reveal";
import { API, BUSINESS, IMAGES } from "../constants/business";

const inputCls =
  "w-full bg-black border border-white/10 focus:border-[#E60000] outline-none text-white placeholder:text-zinc-600 px-4 py-3.5 text-sm transition-colors";

const Contact = () => {
  const [form, setForm] = useState({ name: "", phone: "", email: "", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    document.title = "Contact Us | Mario Auto Body & Collision Repair — Jessup, MD";
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSent(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong. Please try again or call us.");
    } finally {
      setSending(false);
    }
  };

  return (
    <main data-testid="contact-page">
      <section className="relative pt-40 pb-20 overflow-hidden">
        <img src={IMAGES.showroom} alt="" className="absolute inset-0 w-full h-full object-cover opacity-20" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/80 to-black" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10">
          <p className="font-mono2 text-xs tracking-[0.3em] uppercase text-[#E60000] mb-6">Get In Touch</p>
          <h1 className="font-display uppercase leading-[0.85] tracking-tight text-6xl md:text-8xl" data-testid="contact-headline">
            <MaskedLine delay={0.2}>Contact</MaskedLine>
            <MaskedLine delay={0.35}><span className="text-[#E60000]">Us</span></MaskedLine>
          </h1>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-24 md:pb-32">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* INFO */}
          <div>
            <Reveal>
              <div className="grid grid-cols-1 sm:grid-cols-2 border-t border-l border-white/10 mb-12" data-testid="contact-info-grid">
                <div className="border-b border-r border-white/10 p-8" data-testid="contact-address-card">
                  <MapPin size={24} strokeWidth={1.5} className="text-[#E60000] mb-4" />
                  <p className="font-mono2 text-[11px] tracking-[0.2em] uppercase text-zinc-500 mb-2">Address</p>
                  <p className="text-white font-semibold">{BUSINESS.address}</p>
                  <p className="text-white font-semibold">{BUSINESS.city}</p>
                </div>
                <div className="border-b border-r border-white/10 p-8" data-testid="contact-phone-card">
                  <Phone size={24} strokeWidth={1.5} className="text-[#E60000] mb-4" />
                  <p className="font-mono2 text-[11px] tracking-[0.2em] uppercase text-zinc-500 mb-2">Phone</p>
                  <a href={BUSINESS.phoneHref} data-testid="contact-phone-link" className="text-white font-semibold hover:text-[#FF3333] transition-colors">
                    {BUSINESS.phone}
                  </a>
                </div>
                <div className="border-b border-r border-white/10 p-8" data-testid="contact-email-card">
                  <Mail size={24} strokeWidth={1.5} className="text-[#E60000] mb-4" />
                  <p className="font-mono2 text-[11px] tracking-[0.2em] uppercase text-zinc-500 mb-2">Email</p>
                  <p className="text-white font-semibold break-all" data-testid="contact-email-text">{BUSINESS.email}</p>
                </div>
                <div className="border-b border-r border-white/10 p-8" data-testid="contact-hours-card">
                  <Clock size={24} strokeWidth={1.5} className="text-[#E60000] mb-4" />
                  <p className="font-mono2 text-[11px] tracking-[0.2em] uppercase text-zinc-500 mb-2">Business Hours</p>
                  {BUSINESS.hours.map((h) => (
                    <p key={h.days} className="text-sm mb-1">
                      <span className="text-white font-semibold">{h.days}:</span>{" "}
                      <span className={h.time === "Closed" ? "text-zinc-500" : "text-zinc-300"}>{h.time}</span>
                    </p>
                  ))}
                </div>
              </div>
            </Reveal>

            <Reveal delay={0.1}>
              <div className="border border-white/10 overflow-hidden mb-12" data-testid="contact-map">
                <iframe
                  title="Mario Auto Body & Collision Repair location map"
                  src="https://maps.google.com/maps?q=8846%20Washington%20Blvd%2C%20Jessup%2C%20MD%2020794&t=&z=15&ie=UTF8&iwloc=&output=embed"
                  className="w-full h-80 grayscale invert-[0.9] contrast-[0.9]"
                  loading="lazy"
                />
              </div>
            </Reveal>

            <Reveal delay={0.15}>
              <div className="border border-[#E60000]/40 bg-[#E60000]/5 p-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6" data-testid="contact-estimate-cta">
                <div>
                  <h3 className="font-display text-3xl uppercase tracking-wide mb-1">Need repair work?</h3>
                  <p className="text-zinc-400 text-sm">Send your vehicle and damage details in minutes.</p>
                </div>
                <Link
                  to="/estimate"
                  data-testid="contact-submit-estimate-btn"
                  className="shrink-0 inline-flex items-center gap-3 bg-[#E60000] hover:bg-[#FF3333] text-white font-bold uppercase tracking-wider text-sm px-8 py-4 transition-colors"
                >
                  Submit an Estimate <ArrowRight size={16} />
                </Link>
              </div>
            </Reveal>
          </div>

          {/* FORM */}
          <Reveal delay={0.1}>
            <div className="border border-white/10 p-8 md:p-10 lg:sticky lg:top-28">
              {sent ? (
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center py-16" data-testid="contact-confirmation">
                  <CheckCircle2 size={64} strokeWidth={1.2} className="text-[#E60000] mx-auto mb-6" />
                  <h2 className="font-display uppercase text-4xl mb-4">Message Sent</h2>
                  <p className="text-zinc-400">Thanks for reaching out. We'll get back to you as soon as possible.</p>
                </motion.div>
              ) : (
                <form onSubmit={submit} data-testid="contact-form">
                  <SectionLabel>Send a Message</SectionLabel>
                  <div className="flex flex-col gap-6">
                    <div>
                      <label className="block font-mono2 text-[11px] tracking-[0.2em] uppercase text-zinc-500 mb-2">Name <span className="text-[#E60000]">*</span></label>
                      <input data-testid="contact-input-name" required className={inputCls} value={form.name} onChange={set("name")} placeholder="Your name" />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                      <div>
                        <label className="block font-mono2 text-[11px] tracking-[0.2em] uppercase text-zinc-500 mb-2">Phone <span className="text-[#E60000]">*</span></label>
                        <input data-testid="contact-input-phone" required type="tel" className={inputCls} value={form.phone} onChange={set("phone")} placeholder="240-555-0123" />
                      </div>
                      <div>
                        <label className="block font-mono2 text-[11px] tracking-[0.2em] uppercase text-zinc-500 mb-2">Email <span className="text-[#E60000]">*</span></label>
                        <input data-testid="contact-input-email" required type="email" className={inputCls} value={form.email} onChange={set("email")} placeholder="you@email.com" />
                      </div>
                    </div>
                    <div>
                      <label className="block font-mono2 text-[11px] tracking-[0.2em] uppercase text-zinc-500 mb-2">Message <span className="text-[#E60000]">*</span></label>
                      <textarea data-testid="contact-input-message" required rows={6} className={`${inputCls} resize-none`} value={form.message} onChange={set("message")} placeholder="How can we help?" />
                    </div>
                    <button
                      type="submit"
                      disabled={sending}
                      data-testid="contact-send-message-btn"
                      className="w-full bg-[#E60000] hover:bg-[#FF3333] disabled:opacity-60 text-white font-bold uppercase tracking-wider text-sm px-10 py-5 transition-colors inline-flex items-center justify-center gap-3"
                    >
                      {sending && <Loader2 size={16} className="animate-spin" />}
                      {sending ? "Sending..." : "Send Message"}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
};

export default Contact;
